// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { CostInfoTooltip } from '@/components/cost-info-tooltip';
import { SupportSnackbar } from '@/components/support-snackbar';
import { TruncatedText } from '@/components/truncated-text';
import {
  DeleteOutlineRounded,
  EditOutlined,
  HistoryRounded,
  MoreVertRounded,
} from '@/constants/hardfin-standard-icons';
import { Sort } from '@/constants/sort';
import { deleteCost } from '@/costs/api/delete-cost';
import { CostSortProperty } from '@/costs/api/get-costs';
import { Cost } from '@/costs/api/types';
import { CostAuditDrawer } from '@/costs/components/cost-audit-drawer';
import { CostDialog } from '@/costs/components/cost-dialog';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { useDisclosure } from '@/hooks/use-disclosure';
import { useTableSort } from '@/hooks/use-table-sort';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

interface Props {
  sort: Sort<CostSortProperty>;
  onSort: (property: CostSortProperty) => void;
  costs: Cost[];
}

/**
 * Main table component for displaying costs with all required columns:
 * Date, Amount, Description, Person, Project, Category, Info, and Actions
 */
export function CostTable(props: Props) {
  const { getSortDirection, getLabelDirection, isLabelActive } = useTableSort(
    props.sort
  );

  // Create sort handler for column headers
  const createSortHandler = (column: CostSortProperty) => () => {
    props.onSort(column);
  };

  return (
    <TableContainer>
      <Table
        sx={{
          tableLayout: 'fixed',
          width: '100%',
          minWidth: '820px',
          '& .MuiTableCell-root': {
            paddingRight: 0,
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell
              sortDirection={getSortDirection('costIncurredOn')}
              sx={{ width: '128px' }}
            >
              <TableSortLabel
                active={isLabelActive('costIncurredOn')}
                direction={getLabelDirection('costIncurredOn')}
                onClick={createSortHandler('costIncurredOn')}
              >
                Date
              </TableSortLabel>
            </TableCell>
            <TableCell
              sortDirection={getSortDirection('amount')}
              sx={{ width: '124px' }}
              align="right"
            >
              <TableSortLabel
                active={isLabelActive('amount')}
                direction={getLabelDirection('amount')}
                onClick={createSortHandler('amount')}
              >
                Amount
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ width: '250px' }}>Description</TableCell>
            <TableCell
              sortDirection={getSortDirection('person')}
              sx={{ width: '160px' }}
            >
              <TableSortLabel
                active={isLabelActive('person')}
                direction={getLabelDirection('person')}
                onClick={createSortHandler('person')}
              >
                Person
              </TableSortLabel>
            </TableCell>
            <TableCell
              sortDirection={getSortDirection('project')}
              sx={{ width: '180px' }}
            >
              <TableSortLabel
                active={isLabelActive('project')}
                direction={getLabelDirection('project')}
                onClick={createSortHandler('project')}
              >
                Project
              </TableSortLabel>
            </TableCell>
            <TableCell
              sortDirection={getSortDirection('category')}
              sx={{ width: '180px' }}
            >
              <TableSortLabel
                active={isLabelActive('category')}
                direction={getLabelDirection('category')}
                onClick={createSortHandler('category')}
              >
                Category
              </TableSortLabel>
            </TableCell>
            <TableCell align="center" sx={{ width: '112px' }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.costs.map((cost) => (
            <CostTableRow
              key={`${cost.id}-${cost.lastUpdatedAt?.toISOTime() || 'new'}`}
              cost={cost}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

interface CostTableRowProps {
  cost: Cost;
}

/**
 * Individual row component for a cost entry in the table
 */
function CostTableRow(props: CostTableRowProps) {
  const { cost } = props;
  const [menuAnchorEle, setMenuAnchorEle] = useState<null | HTMLElement>(null);
  const auditLogDrawer = useDisclosure();
  const editDialog = useDisclosure();
  const deleteDialog = useDisclosure();
  const queryClient = useQueryClient();

  const deleteCostMutation = useMutation({
    mutationFn: () => deleteCost(cost.key),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['costs'],
      });
      queryClient.invalidateQueries({
        queryKey: ['cost-aggregate'],
      });
      deleteDialog.close();
    },
  });

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setMenuAnchorEle(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEle(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    editDialog.open();
  };

  const handleViewAuditLog = () => {
    handleMenuClose();
    auditLogDrawer.open();
  };

  const handleDelete = () => {
    handleMenuClose();
    deleteDialog.open();
  };

  const handleConfirmDelete = () => {
    deleteCostMutation.mutate();
  };

  return (
    <>
      <SupportSnackbar
        isOpen={deleteCostMutation.isError}
        onClose={deleteCostMutation.reset}
        message="An error occurred while deleting the cost."
      />
      <CostAuditDrawer
        costKey={cost.key}
        isOpen={auditLogDrawer.isOpen}
        onClose={auditLogDrawer.close}
      />
      <CostDialog
        isOpen={editDialog.isOpen}
        onClose={editDialog.close}
        initialCost={{
          key: cost.key,
          amount: cost.amount || undefined,
          description: cost.description || undefined,
          category: cost.category,
          costIncurredOn: cost.costIncurredOn,
          project: cost.attachedProject || undefined,
          person: cost.attachedPerson || undefined,
        }}
        invalidationKeys={[['costs'], ['cost-aggregate']]}
      />
      <Dialog
        fullWidth
        maxWidth="sm"
        open={deleteDialog.isOpen}
        onClose={deleteDialog.close}
      >
        <DialogTitle>Delete cost</DialogTitle>
        <DialogContent dividers>
          <Typography marginBottom={1}>
            Are you sure you want to delete this cost? The cost will be
            permanently removed.
          </Typography>
          <Typography>
            (Note: your audit log will continue to show a record of the deleted
            cost.)
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={deleteDialog.close}>
            No, cancel
          </Button>
          <Button
            size="small"
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={deleteCostMutation.isPending}
          >
            Yes, delete
          </Button>
        </DialogActions>
      </Dialog>
      <TableRow hover>
        <TableCell sx={{ width: '128px' }}>
          <Typography variant="body2" noWrap>
            {cost.costIncurredOn.toFormat('MMM d, yyyy')}
          </Typography>
        </TableCell>
        <TableCell sx={{ width: '124px' }} align="right">
          <Typography variant="body2" noWrap>
            {formatCurrencyValue(cost.amount)}
          </Typography>
        </TableCell>
        <TableCell sx={{ width: '250px' }}>
          {cost.description ? (
            <TruncatedText text={cost.description} variant="body2" />
          ) : (
            <Typography variant="body2">—</Typography>
          )}
        </TableCell>
        <TableCell sx={{ width: '160px' }}>
          {cost.attachedPerson ? (
            (() => {
              const personName = `${cost.attachedPerson.firstName} ${cost.attachedPerson.lastName}`;
              return <TruncatedText text={personName} variant="body2" />;
            })()
          ) : (
            <Typography variant="body2">—</Typography>
          )}
        </TableCell>
        <TableCell sx={{ width: '180px' }}>
          {cost.attachedProject ? (
            <TruncatedText text={cost.attachedProject.name} variant="body2" />
          ) : (
            <Typography variant="body2">—</Typography>
          )}
        </TableCell>
        <TableCell sx={{ width: '180px' }}>
          {cost.category ? (
            <TruncatedText text={cost.category} variant="body2" />
          ) : (
            <Typography variant="body2">—</Typography>
          )}
        </TableCell>
        <TableCell align="center" sx={{ width: '112px' }}>
          <CostInfoTooltip
            createdBy={cost.createdBy}
            createdAt={cost.createdAt}
            lastUpdatedBy={cost.lastUpdatedBy}
            lastUpdatedAt={cost.lastUpdatedAt}
          />
          <IconButton size="small" onClick={handleMenuClick} sx={{ ml: 0.5 }}>
            <MoreVertRounded fontSize="small" />
          </IconButton>
        </TableCell>
      </TableRow>
      <Menu
        anchorEl={menuAnchorEle}
        open={Boolean(menuAnchorEle)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <EditOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleViewAuditLog}>
          <ListItemIcon>
            <HistoryRounded fontSize="small" />
          </ListItemIcon>
          <ListItemText>View audit log</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <ListItemIcon>
            <DeleteOutlineRounded fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
